import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  CardMedia,
  CardActions,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import dayjs from "dayjs";
import { FormattedMessage, FormattedNumber } from "react-intl";
import RevealContactInfo from "./RevealContactInfo";

interface BaseCardProps {
  creatorId: string;
  title: string;
  images?: string[]; // images are optional
  description?: string;
  location: string;
  size?: string; // vehicles don't pass in a size
  price?: string; // houseswaps don't pass in a price
  currency?: string; // no price, no currency.
  listingType?: string; // used to determine if the price is /month /day /night.
  availableFrom?: dayjs.Dayjs;
  availableTo?: dayjs.Dayjs; // only houseswaps pass in availableTo
  topDetails: React.ReactNode;
  details: React.ReactNode;
  canDelete: boolean;
  canModify: boolean;
  onDelete: () => void;
  onEdit: () => void;
}

const BaseCard: React.FC<BaseCardProps> = ({
  creatorId,
  title,
  images,
  description,
  location,
  price,
  currency,
  listingType,
  availableFrom,
  availableTo,
  topDetails,
  details,
  canDelete,
  canModify,
  onDelete,
  onEdit,
}) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const openDialogue = () => {
    setSelectedImage(0);
  };

  const closeDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedImage(null);
  };

  const handleNextImage = (event: React.MouseEvent) => {
    if (images && selectedImage !== null) {
      event.stopPropagation();
      setSelectedImage((prevState) => (prevState! + 1) % images.length);
    }
  };

  const handlePreviousImage = (event: React.MouseEvent) => {
    if (images && selectedImage !== null) {
      event.stopPropagation();
      setSelectedImage(
        (prevState) => (prevState! - 1 + images.length) % images.length
      );
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 5,
        backgroundColor: "background.paper",
        boxShadow: 3,
        transition: "0.3s",
        "&:hover": {
          boxShadow: 6,
        },
      }}
      onClick={openDialogue}
      id="card-listing"
    >
      {images && images.length > 0 && (
        <CardMedia
          component="img"
          height="200"
          image={images[0]}
          alt={title}
          sx={{ objectFit: "cover" }}
        />
      )}
      <Dialog
        open={selectedImage !== null}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        sx={{
          backdropFilter: "blur(5px) sepia(5%)",
        }}
        PaperProps={{ sx: { borderRadius: "10px" } }}
        id="card-dialogue"
      >
        <DialogContent sx={{ padding: 3 }}>
          <IconButton
            id="close-dialogue"
            aria-label="close-dialogue"
            onClick={(event) => {
              closeDialog(event);
            }}
            sx={{
              position: "absolute",
              top: "0px",
              left: "-5px",
              transform: "translateY(-10%)",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Grid container>
            <Grid item xs={12}>
              {selectedImage !== null && images && (
                <Box style={{ position: "relative" }}>
                  <img
                    src={images[selectedImage ?? 0]}
                    alt={title}
                    style={{
                      width: "100%",
                      maxHeight: "500px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                  {images.length > 1 && (
                    <>
                      <IconButton
                        onClick={(event) => {
                          handlePreviousImage(event);
                        }}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "10px",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                          },
                          borderRadius: "50%",
                          width: "50px",
                          height: "50px",
                          paddingLeft: 2,
                        }}
                      >
                        <ArrowBackIosIcon />
                      </IconButton>
                      <IconButton
                        onClick={(event) => {
                          handleNextImage(event);
                        }}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: "10px",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(0, 0, 0, 0.5)", // Transparent dark circle
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                          },
                          borderRadius: "50%", // Makes it a circle
                          width: "50px", // Size of the circle
                          height: "50px",
                        }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item xs={12} marginTop={2} marginBottom={2}>
              <Typography variant="h6" gutterBottom>
                {topDetails}
              </Typography>
              <Divider sx={{ borderColor: "divider", border: "1px solid" }} />
            </Grid>
            <Grid item xs={12} md={6}>
              {details}
              <Typography variant="body1" color="textSecondary">
                <FormattedMessage id="available-from" />{" "}
                {dayjs(availableFrom).format("MMMM D, YYYY")}
              </Typography>
              {availableTo && (
                <Typography variant="body1" color="textSecondary">
                  <FormattedMessage id="end-of-swap" />{" "}
                  {dayjs(availableTo).format("MMMM D, YYYY")}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {description && (
                <Typography variant="body1" color="textSecondary">
                  {description}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  borderColor: "divider",
                  border: "1px solid",
                  marginBottom: 1,
                }}
              />
              <RevealContactInfo creatorId={creatorId} />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" color="textSecondary">
              {location}
            </Typography>
          </Grid>

          {price && (
            <Grid item>
              <Typography variant="h6" color="textSecondary">
                <FormattedNumber
                  value={parseFloat(price)}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency={currency}
                />{" "}
                {(() => {
                  switch (listingType) {
                    case "vehicle":
                      return <FormattedMessage id="per-day" />;
                    default:
                      if (
                        listingType === "long-term" ||
                        listingType === "job"
                      ) {
                        return <FormattedMessage id="per-month" />;
                      }
                      return <FormattedMessage id="per-night" />;
                  }
                })()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions>
        {canDelete && (
          <IconButton
            id="delete-card"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
            color="error"
            aria-label="delete"
            sx={{ ml: "auto" }}
          >
            <DeleteIcon />
          </IconButton>
        )}
        {canModify && (
          <IconButton
            id="edit-card"
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            color="primary"
          >
            <EditIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
};

export default BaseCard;
